import * as React from 'react'
import {View} from 'react-native'
// Based on @react-navigation/native-stack/src/createNativeStackNavigator.ts
// MIT License
// Copyright (c) 2017 React Navigation Contributors
import {
  createNavigatorFactory,
  type EventArg,
  type ParamListBase,
  type StackActionHelpers,
  StackActions,
  type StackNavigationState,
  StackRouter,
  type StackRouterOptions,
  useNavigationBuilder,
} from '@react-navigation/native'
import {
  type NativeStackNavigationEventMap,
  type NativeStackNavigationOptions,
} from '@react-navigation/native-stack'
import {NativeStackView} from '@react-navigation/native-stack'
import {type NativeStackNavigatorProps} from '@react-navigation/native-stack/src/types'

import {PWI_ENABLED} from '#/lib/build-flags'
import {useWebMediaQueries} from '#/lib/hooks/useWebMediaQueries'
import {isNative, isWeb} from '#/platform/detection'
import {usePremium} from '#/state/premium'
import {useSession} from '#/state/session'
import {useOnboardingState} from '#/state/shell'
import {
  useLoggedOutView,
  useLoggedOutViewControls,
} from '#/state/shell/logged-out'
import {LoggedOut} from '#/view/com/auth/LoggedOut'
import {PolicyRightNav} from '#/view/screens/support/PolicyRightNav'
import {Deactivated} from '#/screens/Deactivated'
import {Onboarding} from '#/screens/Onboarding'
import {RoomsRightNav} from '#/screens/Rooms/components/RoomsRightNav'
import {SignupQueued} from '#/screens/SignupQueued'
import {Takendown} from '#/screens/Takendown'
import {AutoScrollProvider} from '#/screens/Videos/AutoScrollContext'
import {VideosRightNav} from '#/screens/Videos/VideosRightNav'
import {atoms as a} from '#/alf'
import {BottomBarWeb, LoggedOutFooter} from './bottom-bar/BottomBarWeb'
import {DesktopLeftNav} from './desktop/LeftNav'
import {DesktopRightNav} from './desktop/RightNav'
import {LoggedOutHeader} from './LoggedOutHeader'

type NativeStackNavigationOptionsWithAuth = NativeStackNavigationOptions & {
  requireAuth?: boolean
}

function NativeStackNavigator({
  id,
  initialRouteName,
  children,
  screenListeners,
  screenOptions,
  ...rest
}: NativeStackNavigatorProps) {
  // --- this is copy and pasted from the original native stack navigator ---
  const {state, descriptors, navigation, NavigationContent} =
    useNavigationBuilder<
      StackNavigationState<ParamListBase>,
      StackRouterOptions,
      StackActionHelpers<ParamListBase>,
      NativeStackNavigationOptionsWithAuth,
      NativeStackNavigationEventMap
    >(StackRouter, {
      id,
      initialRouteName,
      children,
      screenListeners,
      screenOptions,
    })
  React.useEffect(
    () =>
      // @ts-expect-error: there may not be a tab navigator in parent
      navigation?.addListener?.('tabPress', (e: any) => {
        const isFocused = navigation.isFocused()

        // Run the operation in the next frame so we're sure all listeners have been run
        // This is necessary to know if preventDefault() has been called
        requestAnimationFrame(() => {
          if (
            state.index > 0 &&
            isFocused &&
            !(e as EventArg<'tabPress', true>).defaultPrevented
          ) {
            // When user taps on already focused tab and we're inside the tab,
            // reset the stack to replicate native behaviour
            navigation.dispatch({
              ...StackActions.popToTop(),
              target: state.key,
            })
          }
        })
      }),
    [navigation, state.index, state.key],
  )

  // --- our custom logic starts here ---
  const {hasSession, currentAccount} = useSession()
  const {isPremium} = usePremium()
  const activeRoute = state.routes[state.index]
  const activeDescriptor = descriptors[activeRoute.key]
  const activeRouteRequiresAuth = activeDescriptor.options.requireAuth ?? false
  const onboardingState = useOnboardingState()
  const {showLoggedOut} = useLoggedOutView()
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const {isMobile} = useWebMediaQueries()
  if (!hasSession && (!PWI_ENABLED || activeRouteRequiresAuth || isNative)) {
    return <LoggedOut />
  }
  if (hasSession && currentAccount?.signupQueued) {
    return <SignupQueued />
  }
  if (hasSession && currentAccount?.status === 'takendown') {
    return <Takendown />
  }
  if (showLoggedOut) {
    return <LoggedOut onDismiss={() => setShowLoggedOut(false)} />
  }
  if (currentAccount?.status === 'deactivated') {
    return <Deactivated />
  }
  if (onboardingState.isActive) {
    return <Onboarding />
  }
  const newDescriptors: typeof descriptors = {}
  for (let key in descriptors) {
    const descriptor = descriptors[key]
    const requireAuth = descriptor.options.requireAuth ?? false
    newDescriptors[key] = {
      ...descriptor,
      render() {
        if (requireAuth && !hasSession) {
          return <View />
        } else {
          return descriptor.render()
        }
      },
    }
  }

  // Show the bottom bar if we have a session only on mobile web
  const showBottomBar = hasSession && isMobile
  const isRoomsScreen = activeRoute.name === 'Rooms'
  const isVideosScreen = activeRoute.name === 'Videos'
  const isThreeVideosScreen = activeRoute.name === 'ThreeVideos'
  const isPolicyScreen =
    activeRoute.name === 'PrivacyPolicy' ||
    activeRoute.name === 'TermsOfService' ||
    activeRoute.name === 'CommunityGuidelines' ||
    activeRoute.name === 'Disclosure2257' ||
    activeRoute.name === 'CopyrightPolicy' ||
    activeRoute.name === 'Privacy' ||
    activeRoute.name === 'Support' ||
    activeRoute.name === 'About'

  const Wrapper = isVideosScreen ? AutoScrollProvider : React.Fragment

  return (
    <NavigationContent>
      <Wrapper>
        <View role="main" style={[a.flex_1, !hasSession && {paddingTop: 60}]}>
          <NativeStackView
            {...rest}
            state={state}
            navigation={navigation}
            descriptors={newDescriptors}
          />
        </View>
        {isWeb && (
          <>
            {!hasSession && <LoggedOutHeader />}
            {hasSession &&
              (showBottomBar ? <BottomBarWeb /> : <DesktopLeftNav />)}
            {!isMobile &&
              !isThreeVideosScreen &&
              !(isRoomsScreen && !isPremium) &&
              !(isVideosScreen && !isPremium) &&
              (isRoomsScreen ? (
                <RoomsRightNav />
              ) : isVideosScreen ? (
                <VideosRightNav />
              ) : isPolicyScreen ? (
                <PolicyRightNav routeName={activeRoute.name} />
              ) : (
                <DesktopRightNav routeName={activeRoute.name} />
              ))}
            {!hasSession && <LoggedOutFooter />}
          </>
        )}
      </Wrapper>
    </NavigationContent>
  )
}

export const createNativeStackNavigatorWithAuth = createNavigatorFactory<
  StackNavigationState<ParamListBase>,
  NativeStackNavigationOptionsWithAuth,
  NativeStackNavigationEventMap,
  typeof NativeStackNavigator
>(NativeStackNavigator)
